import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
	Alert,
	Button,
	Card,
	Col,
	DatePicker,
	Drawer,
	Form,
	Layout,
	Row,
	Slider,
	Spin,
	Table,
	Tag,
	Tooltip as AntdTooltip,
} from 'antd';
import * as BizCharts from 'bizcharts';
import { connect } from 'react-redux';
import { DownloadOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { numberWithCommas } from '../../utils/DataFormatterUtils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	fetchLifecycleMonthlyApprovalTime,
	fetchLifecyclePrioritySummary,
	fetchLifecycleSupplierPerformance,
} from '../../thunks/lifecycle_analytics_thunks';
import { problemTypesHeirarchicalForBuyer } from '../../thunks/problem_types_thunks';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import {
	FILTER_COLLECTION_NAME,
	DISPLAY_DATE_FORMAT,
	MAX_SUPPLIERS_IN_TABLE,
	MONTH_ABBR,
	MONTH_FULL,
	PERFORMER_GROUP_ORDER,
	PERFORMER_COLOR,
	INTERNAL_TEAM_COLOR,
} from './LifeCycleAnalyticsConstants';
import {
	getDefaultFilters,
	formatSupplierName,
	toMinutes,
	formatDurationShort,
	getPerformerGroupByRank,
	getPositionBasedColor,
	getPositionBasedBgColor,
	filtersToParams,
	filtersToQueryParams,
	queryParamsToFilters,
	buildSupplierTooltipHtml,
} from './LifeCycleAnalyticsUtils';

import { EmptyState } from '../empty_state/EmptyState';
import queryString from 'qs';
import '../charts/chartTheme';
import './registerTopPerformerShape';
import './LifeCycleAnalyticsOverviewPage.less';

const { Content } = Layout;
const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const LifeCycleAnalyticsOverviewPage = (props) => {
	const {
		companyConfig,
		userType,
		problemTypes,
		fetchPrioritySummary,
		fetchSupplierPerformance,
		fetchMonthlyApprovalTime,
		fetchProblemTypes,
	} = props;

	const history = useHistory();
	const location = useLocation();

	const { initialFilters, initialPriorityId, initialMinWoCount } = useMemo(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const qp = queryString.parse(str || '');
		const hasParams = Object.keys(qp).length > 0;
		return {
			initialFilters: hasParams ? queryParamsToFilters(qp) : getDefaultFilters(),
			initialPriorityId: qp.priorityId ? parseInt(String(qp.priorityId), 10) : null,
			initialMinWoCount: qp.minWoCount ? parseInt(String(qp.minWoCount), 10) : 0,
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [loading, setLoading] = useState(true);
	const [chartLoading, setChartLoading] = useState(true);
	const [error, setError] = useState('');
	const [prioritySummary, setPrioritySummary] = useState([]);
	const [supplierPerformance, setSupplierPerformance] = useState([]);
	const [monthlyApprovalTime, setMonthlyApprovalTime] = useState([]);

	const [activeFilters, setActiveFilters] = useState(initialFilters);
	const [draftFilters, setDraftFilters] = useState(initialFilters);
	const [filterModalVisible, setFilterModalVisible] = useState(false);
	const [selectedPriorityId, setSelectedPriorityId] = useState(initialPriorityId);
	const [minWoCount, setMinWoCount] = useState(initialMinWoCount);
	const priorityTabsRef = useRef(null);

	// Sync filters to URL params
	useEffect(() => {
		const params = filtersToQueryParams(activeFilters, selectedPriorityId, minWoCount);
		const search = queryString.stringify(params);
		history.replace(location.pathname + (search ? '?' + search : ''));
	}, [activeFilters, selectedPriorityId, minWoCount]); // eslint-disable-line react-hooks/exhaustive-deps

	// Convert vertical mouse wheel scroll to horizontal scroll on the priority tabs (scrollbar is hidden)
	useEffect(() => {
		const tabsContainer = priorityTabsRef.current;
		if (!tabsContainer) return;
		const handler = (event: WheelEvent) => {
			if (event.deltaY !== 0) {
				tabsContainer.scrollLeft += event.deltaY;
				event.preventDefault();
			}
		};
		tabsContainer.addEventListener('wheel', handler, { passive: false });
		return () => {
			tabsContainer.removeEventListener('wheel', handler);
		};
	}, []);

	const isAutoApprovalEnabled = useMemo(() => {
		const approvalConfig = nullSafeGetOrElse('detail.config.approvalConfig', companyConfig, {});
		return (
			nullSafeGetOrElse('autoApproveServiceRequests', approvalConfig, false) &&
			nullSafeGetOrElse('autoApproveSupplierInitiatedWorkOrders', approvalConfig, false)
		);
	}, [companyConfig]);

	const loadChartData = useCallback(
		(filters, priorityId) => {
			setChartLoading(true);
			const params = filtersToParams(filters);
			if (priorityId !== null) {
				params.woPriorityId = priorityId;
			}
			Promise.all([fetchSupplierPerformance({}, params), fetchMonthlyApprovalTime({}, params)])
				.then(([supplierData, monthlyApprovalData]) => {
					setSupplierPerformance(supplierData || []);
					setMonthlyApprovalTime(monthlyApprovalData || []);
				})
				.catch((e) => {
					setError(e || 'Failed to load analytics data.');
				})
				.finally(() => {
					setChartLoading(false);
				});
		},
		[fetchSupplierPerformance, fetchMonthlyApprovalTime]
	);

	// Priority summary first, then one chart fetch with the resolved priority (avoids racing two chart loads).
	const loadAllData = useCallback(
		(filters, priorityIdHint) => {
			setLoading(true);
			setChartLoading(true);
			setError('');
			const params = filtersToParams(filters);
			fetchPrioritySummary({}, params)
				.then((priorityData) => {
					const data = priorityData || [];
					setPrioritySummary(data);
					const sorted = data.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
					let effectivePriorityId = null;
					if (sorted.length > 0) {
						const hintIsValid =
							priorityIdHint !== null &&
							priorityIdHint !== undefined &&
							sorted.some((row) => row.woPriorityId === priorityIdHint);
						effectivePriorityId = hintIsValid ? priorityIdHint : sorted[0].woPriorityId;
					}
					setSelectedPriorityId(effectivePriorityId);
					const chartParams = filtersToParams(filters);
					if (effectivePriorityId !== null) {
						chartParams.woPriorityId = effectivePriorityId;
					}
					return Promise.all([
						fetchSupplierPerformance({}, chartParams),
						fetchMonthlyApprovalTime({}, chartParams),
					]);
				})
				.then((chartResult) => {
					if (!chartResult) {
						return;
					}
					const [supplierData, monthlyApprovalData] = chartResult;
					setSupplierPerformance(supplierData || []);
					setMonthlyApprovalTime(monthlyApprovalData || []);
				})
				.catch((e) => {
					setError(e || 'Failed to load Life Cycle Analytics data.');
				})
				.finally(() => {
					setLoading(false);
					setChartLoading(false);
				});
		},
		[fetchPrioritySummary, fetchSupplierPerformance, fetchMonthlyApprovalTime]
	);

	// Initial load only. Filter/priority changes call loadAllData/loadChartData directly from handlers.
	useEffect(() => {
		loadAllData(initialFilters, initialPriorityId);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const openFilterModal = () => {
		setDraftFilters({ ...activeFilters });
		setFilterModalVisible(true);
	};

	const applyFilters = () => {
		setActiveFilters(draftFilters);
		setFilterModalVisible(false);
		loadAllData(draftFilters, selectedPriorityId);
	};

	const cancelFilters = () => {
		setFilterModalVisible(false);
	};

	const removeProblemTypeFilter = () => {
		const newFilters = { ...activeFilters, problemTypeIds: [] };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const removeDateFilter = (field) => {
		const newFilters = { ...activeFilters, [field]: null };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const handlePriorityTabClick = (priorityId) => {
		const newId = selectedPriorityId === priorityId ? null : priorityId;
		setSelectedPriorityId(newId);
		loadChartData(activeFilters, newId);
	};

	const hasActiveFilters =
		activeFilters.problemTypeIds.length > 0 ||
		activeFilters.createdAt !== null ||
		activeFilters.completedAt !== null;

	const problemTypeNames = useMemo(() => {
		return activeFilters.problemTypeIds
			.map((id) => nullSafeGetOrElse('name', problemTypes.records[id], null))
			.filter(Boolean)
			.join(', ');
	}, [activeFilters.problemTypeIds, problemTypes.records]);

	const totalCompletedWorkOrders = useMemo(() => {
		return prioritySummary.reduce((sum, item) => sum + (item.woCount || 0), 0);
	}, [prioritySummary]);

	const prioritySummaryCards = useMemo(() => {
		const sorted = prioritySummary.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
		const total = sorted.length;
		return sorted.map((item, index) => {
			const share =
				totalCompletedWorkOrders > 0
					? Math.round(((item.woCount || 0) * 10000) / totalCompletedWorkOrders) / 100
					: 0;
			return {
				...item,
				color: getPositionBasedColor(index, total),
				bgColor: getPositionBasedBgColor(index, total),
				share,
			};
		});
	}, [prioritySummary, totalCompletedWorkOrders]);

	const selectedPriorityCard = useMemo(
		() => prioritySummaryCards.find((c) => c.woPriorityId === selectedPriorityId) || null,
		[prioritySummaryCards, selectedPriorityId]
	);

	// Note: this is an unweighted average of monthly averages. If the backend exposes
	// woCount per month, this should be replaced with a weighted average.
	const avgApprovalSeconds = useMemo(() => {
		const valid = monthlyApprovalTime.filter(
			(item) => item && item.avgApprovalTimeSeconds !== null
		);
		if (valid.length === 0) return 0;
		return Math.round(
			valid.reduce((sum, item) => sum + (item.avgApprovalTimeSeconds || 0), 0) / valid.length
		);
	}, [monthlyApprovalTime]);

	const monthlyApprovalChartData = useMemo(() => {
		return monthlyApprovalTime
			.filter((item) => item && item.avgApprovalTimeSeconds !== null)
			.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item) => ({
				monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
				monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
				avgApprovalMinutes: toMinutes(item.avgApprovalTimeSeconds),
				avgApprovalTimeSeconds: item.avgApprovalTimeSeconds,
			}));
	}, [monthlyApprovalTime]);

	const supplierPerformanceAnalytics = useMemo(() => {
		const validRows = supplierPerformance.filter(
			(row) =>
				row && row.avgResolutionTimeSeconds !== null && row.avgResolutionTimeSeconds !== undefined
		);
		const totalRows = validRows.length;
		return validRows.map((row, index) => ({
			supplierFacilityId: row.supplierFacilityId,
			supplierFacilityName: formatSupplierName(row),
			woCount: row.woCount || 0,
			avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
			avgResolutionMinutes: toMinutes(row.avgResolutionTimeSeconds),
			avgResponseTimeSeconds: row.avgResponseTimeSeconds || 0,
			avgResponseTimeMinutes: toMinutes(row.avgResponseTimeSeconds || 0),
			isInternalTeam: !!row.isInternalTeam,
			performerGroup: row.isInternalTeam
				? 'Top Performers'
				: getPerformerGroupByRank(index, totalRows),
		}));
	}, [supplierPerformance]);

	const filteredSupplierPerformance = useMemo(() => {
		if (minWoCount <= 0) return supplierPerformanceAnalytics;
		return supplierPerformanceAnalytics.filter((row) => row.woCount >= minWoCount);
	}, [supplierPerformanceAnalytics, minWoCount]);

	const exportApprovalTimeCsv = useCallback(() => {
		if (monthlyApprovalChartData.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Average_Approval_Time_' + priorityLabel.replace(/\s+/g, '_'),
			headers: ['Month', 'Avg Approval Time (minutes)', 'Avg Approval Time'],
		});
		const data = monthlyApprovalChartData.map((row) => {
			return {
				month: row.monthFull,
				avgApprovalMinutes: Math.round(row.avgApprovalMinutes),
				avgApprovalFormatted: formatDurationShort(row.avgApprovalTimeSeconds),
			};
		});
		csvExporter.generateCsv(data);
	}, [monthlyApprovalChartData, selectedPriorityCard]);

	const exportSupplierPerformanceCsv = useCallback(() => {
		const dataToExport = filteredSupplierPerformance;
		if (dataToExport.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'WO_Performance_' + priorityLabel.replace(/\s+/g, '_'),
			headers: [
				'Supplier',
				'WOs',
				'Avg Response Time (seconds)',
				'Avg Response Time',
				'Avg Resolution Time (seconds)',
				'Avg Resolution Time',
				'Performer Group',
			],
		});
		const data = dataToExport.map((row) => {
			return {
				supplier: row.supplierFacilityName,
				woCount: row.woCount,
				avgResponseTimeSeconds: row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationShort(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationShort(row.avgResolutionTimeSeconds),
				performerGroup: row.performerGroup,
			};
		});
		csvExporter.generateCsv(data);
	}, [filteredSupplierPerformance, selectedPriorityCard]);

	return (
		<Content className="lifeCycleAnalyticsOverviewPage">
			<div
				className="lifeCyclePageHeader"
				style={{ background: nullSafeGet('detail.config.theme.navblock', companyConfig) }}
			>
				<div className="lifeCyclePageTitle">Life Cycle Analytics</div>
			</div>
			<Spin spinning={loading || chartLoading}>
				{error ? <Alert type="error" message={error} className="lifeCycleErrorAlert" /> : null}

				<Row gutter={16} className="lifeCycleCardRow">
					<Col span={24}>
						<Card className="lifeCycleSummaryCard">
							<div className="lifeCycleSummaryHeader">
								<div>Completed Work Orders: {numberWithCommas(totalCompletedWorkOrders)}</div>
								<Button icon={<FilterOutlined />} onClick={openFilterModal} />
							</div>
							{hasActiveFilters && (
								<div className="mb-4 mt-2">
									{activeFilters.problemTypeIds.length > 0 && (
										<Tag closable onClose={removeProblemTypeFilter}>
											Problem Type:{' '}
											{problemTypeNames || `${activeFilters.problemTypeIds.length} selected`}
										</Tag>
									)}
									{activeFilters.createdAt &&
										activeFilters.createdAt[0] &&
										activeFilters.createdAt[1] && (
											<Tag closable onClose={() => removeDateFilter('createdAt')}>
												Created between: {activeFilters.createdAt[0].format(DISPLAY_DATE_FORMAT)} to{' '}
												{activeFilters.createdAt[1].format(DISPLAY_DATE_FORMAT)}
											</Tag>
										)}
									{activeFilters.completedAt &&
										activeFilters.completedAt[0] &&
										activeFilters.completedAt[1] && (
											<Tag closable onClose={() => removeDateFilter('completedAt')}>
												Completed between:{' '}
												{activeFilters.completedAt[0].format(DISPLAY_DATE_FORMAT)} to{' '}
												{activeFilters.completedAt[1].format(DISPLAY_DATE_FORMAT)}
											</Tag>
										)}
								</div>
							)}
							{!loading && totalCompletedWorkOrders === 0 ? (
								<EmptyState
									height={200}
									headline={'No completed work orders found'}
									body={
										<div style={{ maxWidth: 488, textAlign: 'center' }}>
											There are no completed work orders for the selected filters. Try adjusting
											your filters or expanding the date range.
										</div>
									}
								/>
							) : (
								<>
									<div className="lifeCycleProgressBar">
										{prioritySummaryCards.map((item) => (
											<div
												key={item.woPriorityId}
												style={{ width: `${item.share}%`, background: item.color }}
												className="lifeCycleProgressItem"
											/>
										))}
									</div>
									<div className="lifeCyclePriorityTabs" ref={priorityTabsRef}>
										{prioritySummaryCards.map((item) => {
											const isSelected = selectedPriorityId === item.woPriorityId;
											return (
												<div
													key={item.woPriorityId}
													className={`lifeCyclePriorityTab${
														isSelected ? ' lifeCyclePriorityTab--selected' : ''
													}`}
													onClick={() => handlePriorityTabClick(item.woPriorityId)}
												>
													<div className="priorityTitleRow">
														<div className="flex h-[22px] w-full min-w-0 flex-row items-center gap-2">
															<span
																className="priorityDot flex-shrink-0"
																style={{ background: item.color }}
															/>
															<AntdTooltip title={item.woPriorityName}>
																<div className="line-height-[22px] min-w-0 flex-1 truncate text-[14px] text-black">
																	{item.woPriorityName}
																</div>
															</AntdTooltip>
														</div>
														<div className="priorityMetric">
															{numberWithCommas(item.woCount || 0)}{' '}
															<span className="line-height-[22px] text-[14px] font-normal text-black">
																WOs
															</span>
														</div>
													</div>
													<div className="flex h-[66px] w-[202.8px] flex-col">
														{item.avgApprovalTimeSeconds !== undefined &&
															item.avgApprovalTimeSeconds !== null && (
																<div className="prioritySubMetric">
																	Avg Approval time:{' '}
																	{formatDurationShort(item.avgApprovalTimeSeconds || 0)}
																</div>
															)}
														{item.avgResponseTimeSeconds !== undefined &&
															item.avgResponseTimeSeconds !== null && (
																<div className="prioritySubMetric">
																	Avg Response time:{' '}
																	{formatDurationShort(item.avgResponseTimeSeconds || 0)}
																</div>
															)}
														<div className="prioritySubMetric">
															Avg Resolution time:{' '}
															{formatDurationShort(item.avgResolutionTimeSeconds || 0)}
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</>
							)}
						</Card>
					</Col>
				</Row>

				{!isAutoApprovalEnabled && totalCompletedWorkOrders > 0 && (
					<Row gutter={16} className="lifeCycleCardRow">
						<Col span={24}>
							<Card
								title={
									<div className="woPerformanceTitle">
										<span className="woPerformanceTitleText">
											Average Approval Time: {formatDurationShort(avgApprovalSeconds)}
										</span>
										{selectedPriorityCard && (
											<span className="woPerformancePriorityBadge">
												<span
													className="woPerformancePriorityDot"
													style={{ background: selectedPriorityCard.color }}
												/>
												{selectedPriorityCard.woPriorityName}
											</span>
										)}
									</div>
								}
								extra={<Button icon={<DownloadOutlined />} onClick={exportApprovalTimeCsv} />}
							>
								<Chart
									height={360}
									padding="auto"
									data={monthlyApprovalChartData}
									autoFit={true}
									forceFit={true}
									theme="openwrench"
									scale={{
										avgApprovalMinutes: {
											min: 0,
											nice: true,
										},
									}}
								>
									<Axis
										name="monthLabel"
										title={{
											text: 'Month',
											style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
											offset: 40,
										}}
										line={{ style: { stroke: '#e8e8e8' } }}
										tickLine={null}
										label={{
											style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
										}}
									/>
									<Axis
										name="avgApprovalMinutes"
										title={{
											text: 'Avg. Approval Time',
											style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
											offset: 80,
										}}
										label={{
											formatter: (val) => `${val}m`,
											style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
										}}
										line={null}
										tickLine={null}
										grid={{
											line: {
												style: {
													stroke: '#e8e8e8',
													lineDash: [4, 4],
												},
											},
										}}
									/>
									<Tooltip crosshairs={{ type: 'y' }} />
									<Geom
										type="line"
										position="monthLabel*avgApprovalMinutes"
										color={selectedPriorityCard ? selectedPriorityCard.color : PERFORMER_COLOR}
										size={2}
										tooltip={[
											'monthFull*avgApprovalMinutes',
											(monthFull, mins) => ({
												title: monthFull,
												name: 'Avg approval time',
												value: `${Math.round(mins)}m`,
											}),
										]}
									/>
									<Geom
										type="point"
										position="monthLabel*avgApprovalMinutes"
										color={selectedPriorityCard ? selectedPriorityCard.color : PERFORMER_COLOR}
										size={5}
										shape={'circle'}
										style={{
											fill: '#fff',
											stroke: selectedPriorityCard ? selectedPriorityCard.color : PERFORMER_COLOR,
											lineWidth: 2,
										}}
										tooltip={false}
									/>
								</Chart>
							</Card>
						</Col>
					</Row>
				)}

				{totalCompletedWorkOrders > 0 && (
					<Row gutter={16} className="lifeCycleCardRow">
						<Col span={24}>
							<Card
								title={
									<div className="woPerformanceTitle">
										<span className="woPerformanceTitleText">WO Performance</span>
										{selectedPriorityCard && (
											<span className="woPerformancePriorityBadge">
												<span
													className="woPerformancePriorityDot"
													style={{ background: selectedPriorityCard.color }}
												/>
												{selectedPriorityCard.woPriorityName}
											</span>
										)}
										<div className="woPerformanceSliderContainer">
											<AntdTooltip title="Filter suppliers by minimum number of completed work orders">
												<InfoCircleOutlined className="woPerformanceInfoIcon" />
											</AntdTooltip>
											<span className="woPerformanceSliderLabel">
												Minimum WOs completed by supplier:
											</span>
											<Slider
												min={0}
												max={1000}
												step={10}
												value={minWoCount}
												onChange={(val) => setMinWoCount(val)}
												className="woPerformanceSlider"
											/>
											<span className="woPerformanceSliderValue">{minWoCount}</span>
										</div>
									</div>
								}
								extra={
									<Button icon={<DownloadOutlined />} onClick={exportSupplierPerformanceCsv} />
								}
							>
								<Row gutter={0} className="woPerformanceChartRow">
									<Col span={14} className="woPerformanceChartCol">
										{filteredSupplierPerformance.length > MAX_SUPPLIERS_IN_TABLE && (
											<Alert
												className="woPerformanceDataAlert"
												type="error"
												banner
												showIcon={false}
												closable={false}
												message={`Too much data to display. showing ${MAX_SUPPLIERS_IN_TABLE}/${filteredSupplierPerformance.length} suppliers. Filter to narrow selection`}
											/>
										)}
										<Chart
											height={460}
											padding="auto"
											data={filteredSupplierPerformance.slice(0, MAX_SUPPLIERS_IN_TABLE)}
											autoFit={true}
											theme="openwrench"
											scale={{
												performerGroup: {
													values: PERFORMER_GROUP_ORDER,
												},
											}}
											onPointClick={(ev) => {
												const data = nullSafeGet('data.data', ev);
												const supplierFacilityId = data && data.supplierFacilityId;
												if (supplierFacilityId) {
													const filterParams = filtersToQueryParams(
														activeFilters,
														selectedPriorityId,
														minWoCount
													);
													const pathName = '/' + userType + '/analytics/lifecycle/overview';
													const backlinkParts = {
														pathName: pathName,
														backLinkText: 'Back to Life Cycle Analytics',
														...filterParams,
													};
													const backlinkParams = encodeURIComponent(
														queryString.stringify(backlinkParts)
													);
													history.push(
														'/' +
															userType +
															'/analytics/kpi/vendors/detail/' +
															supplierFacilityId +
															'?backlinkParams=' +
															backlinkParams
													);
												}
											}}
										>
											<Axis
												name="avgResponseTimeMinutes"
												title={{
													text: 'Avg. Response Time',
													style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
													offset: 40,
												}}
												label={{
													formatter: (val) => `${val}m`,
													style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
												}}
												line={{ style: { stroke: '#e8e8e8' } }}
												tickLine={null}
											/>
											<Axis
												name="avgResolutionMinutes"
												title={{
													text: 'Avg. Resolution Time',
													style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
													offset: 80,
												}}
												label={{
													formatter: (val) => `${val}m`,
													style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
												}}
												line={null}
												tickLine={null}
												grid={{
													line: {
														style: {
															stroke: '#e8e8e8',
															lineDash: [4, 4],
														},
													},
												}}
											/>
											<Legend position="bottom" />
											<Tooltip
												showTitle={false}
												crosshairs={false}
												itemTpl={
													'<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>'
												}
												domStyles={{
													'g2-tooltip': {
														padding: '0',
														boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
														borderRadius: '4px',
													},
													'g2-tooltip-list': {
														margin: '0',
														padding: '0',
													},
												}}
											/>
											<Geom
												type="point"
												position="avgResponseTimeMinutes*avgResolutionMinutes"
												color={PERFORMER_COLOR}
												shape={[
													'performerGroup*isInternalTeam',
													(performerGroup, isInternalTeam) => {
														if (isInternalTeam) return 'topPerformerStar';
														if (performerGroup === 'Top Performers') return 'topPerformerStar';
														if (performerGroup === 'Bottom Performers') return 'cross';
														return 'hollowCircle';
													},
												]}
												size={[
													'performerGroup*isInternalTeam',
													(performerGroup, isInternalTeam) => {
														if (isInternalTeam || performerGroup === 'Top Performers') return 6;
														return 5;
													},
												]}
												tooltip={[
													'supplierFacilityName*woCount*avgResponseTimeSeconds*avgResolutionTimeSeconds',
													(name, woCount, responseSeconds, resolutionSeconds) => ({
														name: name,
														value: buildSupplierTooltipHtml(
															name,
															woCount,
															responseSeconds,
															resolutionSeconds
														),
													}),
												]}
												style={[
													'performerGroup*isInternalTeam',
													(performerGroup, isInternalTeam) => {
														if (isInternalTeam) {
															return {
																lineWidth: 1.5,
																fill: INTERNAL_TEAM_COLOR,
																stroke: INTERNAL_TEAM_COLOR,
															};
														}
														if (performerGroup === 'Top Performers') {
															return { fill: PERFORMER_COLOR };
														}
														if (performerGroup === 'Typical Performers') {
															return {
																lineWidth: 1.5,
																fill: 'transparent',
																stroke: PERFORMER_COLOR,
															};
														}
														if (performerGroup === 'Bottom Performers') {
															return {
																lineWidth: 1.5,
																fill: null,
																stroke: PERFORMER_COLOR,
															};
														}
														return {
															lineWidth: 1.5,
															fill: INTERNAL_TEAM_COLOR,
															stroke: INTERNAL_TEAM_COLOR,
														};
													},
												]}
											/>
										</Chart>
									</Col>
									<Col span={10}>
										<Table
											columns={[
												{
													title: `Suppliers (${Math.min(
														filteredSupplierPerformance.length,
														MAX_SUPPLIERS_IN_TABLE
													)})`,
													dataIndex: 'supplierFacilityName',
													key: 'supplierFacilityName',
												},
												{
													title: 'WOs',
													dataIndex: 'woCount',
													key: 'woCount',
													sorter: (a, b) => a.woCount - b.woCount,
													render: (value) => numberWithCommas(value || 0),
												},
												{
													title: 'Response time [Avg]',
													dataIndex: 'avgResponseTimeSeconds',
													key: 'avgResponseTimeSeconds',
													sorter: (a, b) => a.avgResponseTimeSeconds - b.avgResponseTimeSeconds,
													render: (value) => formatDurationShort(value || 0),
												},
												{
													title: 'Resolution time [Avg]',
													dataIndex: 'avgResolutionTimeSeconds',
													key: 'avgResolutionTimeSeconds',
													sorter: (a, b) => a.avgResolutionTimeSeconds - b.avgResolutionTimeSeconds,
													render: (value) => formatDurationShort(value || 0),
												},
											]}
											rowKey={(row) => `${row.supplierFacilityId}`}
											pagination={false}
											size="small"
											scroll={{ y: 420 }}
											dataSource={filteredSupplierPerformance.slice(0, MAX_SUPPLIERS_IN_TABLE)}
										/>
									</Col>
								</Row>
							</Card>
						</Col>
					</Row>
				)}
			</Spin>

			<Drawer
				title="Filters"
				placement="right"
				closable={false}
				width={1000}
				visible={filterModalVisible}
				onClose={cancelFilters}
			>
				<Form layout="vertical">
					<Form.Item label="Problem Type">
						<ProblemTypeTreeSelect
							stateSlice={problemTypes}
							targetCollectionName={FILTER_COLLECTION_NAME}
							fetchData={fetchProblemTypes}
							fetchMultiple={() => {}}
							mode="multiple"
							allowClear={true}
							value={draftFilters.problemTypeIds}
							onChange={(ids) => setDraftFilters({ ...draftFilters, problemTypeIds: ids || [] })}
							style={{ width: '100%' }}
						/>
					</Form.Item>
					<Form.Item label="Created between">
						<DatePicker.RangePicker
							style={{ width: '100%' }}
							value={draftFilters.createdAt}
							format={DISPLAY_DATE_FORMAT}
							onChange={(dates) => setDraftFilters({ ...draftFilters, createdAt: dates })}
							allowEmpty={[true, true]}
						/>
					</Form.Item>
					<Form.Item label="Completed between">
						<DatePicker.RangePicker
							style={{ width: '100%' }}
							value={draftFilters.completedAt}
							format={DISPLAY_DATE_FORMAT}
							onChange={(dates) => setDraftFilters({ ...draftFilters, completedAt: dates })}
							allowEmpty={[true, true]}
						/>
					</Form.Item>
				</Form>
				<div className="mt-6 flex gap-2">
					<Button type="primary" onClick={applyFilters}>
						UPDATE FILTERS
					</Button>
					<Button onClick={cancelFilters}>CANCEL</Button>
				</div>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	userType: (state as any).session.userType,
	companyConfig: state.company_config,
	problemTypes: state.problem_types,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

const mergeProps = (stateProps, dispatchProps, ownProps) => {
	const { userType } = stateProps;
	const { dispatch } = dispatchProps;
	return {
		...ownProps,
		...stateProps,
		fetchPrioritySummary: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecyclePrioritySummary(userType)(params, filters, pagination, sorting)),
		fetchSupplierPerformance: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleSupplierPerformance(userType)(params, filters, pagination, sorting)),
		fetchMonthlyApprovalTime: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleMonthlyApprovalTime(userType)(params, filters, pagination, sorting)),
		fetchProblemTypes: (
			searchText,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				problemTypesHeirarchicalForBuyer(
					typeof searchText === 'string' && searchText ? { name: searchText } : {},
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(LifeCycleAnalyticsOverviewPage);
